import {AuthModel} from './_models';
import { HybridAuthApiModule } from '../../../ApiHelper/HybridAuthApiModule';

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v';
const CURRENT_USER_DETAILS = 'current-user';
let authRequestInterceptorId: number | null = null;
let authErrorInterceptorId: number | null = null;

const getLegacyAuth = (): AuthModel | undefined => {
  if (typeof localStorage === 'undefined') {
    return;
  }

  const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY);
  if (!lsValue) {
    return;
  }

  try {
    const auth: AuthModel = JSON.parse(lsValue) as AuthModel;
    if (auth) {
      return auth;
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE PARSE ERROR', error);
  }
};

const getAuth = (): AuthModel | undefined => {
  return getLegacyAuth();
};

const authModule = new HybridAuthApiModule();

// Firebase ID tokens expire hourly; always ask the SDK for a current one
// rather than reusing the copy saved in localStorage at login.
export const getFirebaseToken = async (): Promise<string | undefined> => {
  try {
    return await authModule.getIdToken();
  } catch (error) {
    console.error('FIREBASE TOKEN FETCH ERROR', error);
  }
};

const setAuth = (auth: AuthModel) => {
  if (!localStorage) {
    return;
  }

  try {
    const lsValue = JSON.stringify(auth);
    localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue);
  } catch (error) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR', error);
  }
};
const setCurrentUser = (auth: AuthModel | undefined) => {
  if (!localStorage) {
    return;
  }

  try {
    const lsValue = JSON.stringify(auth);
    localStorage.setItem(CURRENT_USER_DETAILS, lsValue);
  } catch (error) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR', error);
  }
};

const getCurrentUser = (): AuthModel | undefined => {
  if (!localStorage) {
    return;
  }

  const lsValue: string | null = localStorage.getItem(CURRENT_USER_DETAILS);
  if (!lsValue) {
    return;
  }

  try {
    const auth: AuthModel = JSON.parse(lsValue) as AuthModel;
    if (auth) {
      // You can easily check auth_token expiration also
      return auth;
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE PARSE ERROR', error);
  }
};

const removeAuth = () => {
  if (!localStorage) {
    return;
  }

  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY);
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error);
  }
};

export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = 'application/json';

  if (authRequestInterceptorId !== null) {
    axios.interceptors.request.eject(authRequestInterceptorId);
  }

  authRequestInterceptorId = axios.interceptors.request.use(
    async (config: {headers: {Authorization: string}}) => {
      const firebaseToken = await getFirebaseToken();
      if (firebaseToken) {
        config.headers.Authorization = `Bearer ${firebaseToken}`;
        return config;
      }

      const auth = getAuth();
      if (auth?.token) {
        config.headers.Authorization = `Bearer: ${auth.token}`;
      }

      return config;
    },
    (err: any) => Promise.reject(err)
  );
}

export function setupAxiosErr(axios: any, navigate: any, toast: any, logout, setShowErrModal) {
  if (authErrorInterceptorId !== null) {
    axios.interceptors.response.eject(authErrorInterceptorId);
  }

  authErrorInterceptorId = axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const auth = getAuth();
      const status = error?.response?.status;

      // 401 / 601 (token invalid or expired) are handled by axiosSetup.ts,
      // which signs out of Firebase, clears local state, and full-reloads
      // to /auth/login. Don't double-handle here.
      if (auth && auth.roleName && status === 600) {
        localStorage.setItem('gotErr', JSON.stringify(error));
        setShowErrModal(true);
      }
      throw error;
    },
    (err: any) => Promise.reject(err)
  );
}
export {getAuth, setAuth, removeAuth, AUTH_LOCAL_STORAGE_KEY, setCurrentUser, getCurrentUser};
